import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { relativeTime } from "@/lib/format";
import { GlowDot, type Tone } from "@/design/primitives";

const READ_KEY = "af.notifications.readTs";

/**
 * Notifikační zvonek: Fáze 1 čerpá z audit eventů (poslední aktivita).
 * Severity-aware notifikační kanál + push přijdou ve Fázi 9.
 */
/**
 * `placement` říká, odkud zvonek visí: v horní liště se panel rozevíral pod
 * ní, v railu musí ven doprava — jinak by vylezl mimo obrazovku.
 */
export function NotificationsPopover({ placement = "bar" }: { placement?: "bar" | "rail" } = {}) {
  const [open, setOpen] = useState(false);
  const [readTs, setReadTs] = useState<string>(() => localStorage.getItem(READ_KEY) ?? "");

  const query = useQuery({
    queryKey: ["audit", "notifications"],
    queryFn: () => api.audit({ limit: 6 }),
    refetchInterval: 60_000,
  });

  const events = query.data?.events ?? [];
  const unread = useMemo(
    () => events.filter((e) => !readTs || e.ts > readTs).length,
    [events, readTs],
  );

  const markRead = () => {
    const latest = events[0]?.ts ?? new Date().toISOString();
    localStorage.setItem(READ_KEY, latest);
    setReadTs(latest);
  };

  return (
    <div className="relative">
      <button
        type="button"
        className={
          placement === "rail"
            ? "relative flex h-11 w-11 cursor-pointer items-center justify-center text-[17px]"
            : "relative cursor-pointer p-1 text-[14px]"
        }
        style={{ color: "var(--text-tertiary)", borderRadius: 14 }}
        aria-label={cs.notifications.title}
        onClick={() => setOpen((v) => !v)}
        data-testid="notifications-toggle"
      >
        ◷
        {unread > 0 ? (
          <span
            className="absolute -right-1 top-0 flex h-[13px] w-[13px] items-center justify-center rounded-full text-[11px] font-bold"
            style={{ background: "var(--critical)", color: "var(--text-on-accent)" }}
          >
            {unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className={`af-animate-fade absolute z-50 w-[380px] max-w-[calc(100vw-24px)] ${
            placement === "rail" ? "bottom-0 left-[42px]" : "right-0 top-9"
          }`}
          style={{
            border: "1px solid var(--border-default)",
            borderRadius: "var(--radius-card)",
            background: "var(--surface-overlay)",
            boxShadow: "var(--shadow-panel)",
            backdropFilter: "var(--surface-blur)",
          }}
        >
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderBottom: "1px solid var(--border-subtle)" }}
          >
            <span className="af-label">{cs.notifications.title}</span>
            <button
              type="button"
              className="cursor-pointer text-[11px] tracking-[0.1em]"
              style={{ color: "var(--text-tertiary)" }}
              onClick={markRead}
            >
              {cs.notifications.markRead}
            </button>
          </div>
          <div>
            {events.length === 0 ? (
              <div className="px-4 py-5 text-[12px]" style={{ color: "var(--text-tertiary)" }}>
                {cs.notifications.empty}
              </div>
            ) : (
              events.map((e) => (
                <div
                  key={e.id}
                  className="flex gap-3 px-4 py-2.5"
                  style={{ borderBottom: "1px solid color-mix(in srgb, var(--accent) 5%, transparent)" }}
                >
                  <div className="pt-1">
                    <GlowDot tone={actionTone(e.action)} pulse={false} size={6} />
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-[12px]" style={{ color: "var(--text-secondary)" }}>
                      {e.projectName ? `${e.projectName}: ` : ""}
                      {e.summary}
                    </div>
                    <div className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                      {e.action.toLowerCase().replaceAll("_", " ")} · {relativeTime(e.ts)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="px-4 py-2.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            {cs.notifications.note}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function actionTone(action: string): Tone {
  if (action.includes("INCIDENT") || action.includes("FAILED")) return "red";
  if (action.includes("PROPOSAL") || action.includes("RISK")) return "amber";
  if (action.includes("PASSED") || action.includes("DONE")) return "green";
  return "cyan";
}
